import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';

function BatchView({ apiBase, loading, setLoading, error, setError }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBase}/analytics/by-batch`);
      setData(response.data);
    } catch (err) {
      setError('Failed to load batch data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="loading">No batch data available</div>;
  }

  const totalCalls = data.reduce((sum, d) => sum + d.total_calls, 0);
  const totalCredits = data.reduce((sum, d) => sum + d.total_credits_used, 0);
  const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0);

  const labels = data.map(d => d.batch_call_name || d.batch_call_id);

  const callsChartData = {
    labels: labels,
    datasets: [{
      label: 'Calls',
      data: data.map(d => d.total_calls),
      backgroundColor: '#667eea'
    }]
  };

  const costChartData = {
    labels: labels,
    datasets: [{
      label: 'Cost ($)',
      data: data.map(d => d.total_cost / 100),
      backgroundColor: '#f56565'
    }]
  };

  const successChartData = {
    labels: labels,
    datasets: [{
      label: 'Success Rate (%)',
      data: data.map(d => d.success_rate),
      backgroundColor: '#48bb78'
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  };

  const costOptions = {
    ...barOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toFixed(2);
          }
        }
      }
    }
  };

  const successOptions = {
    ...barOptions,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Batches</h3>
          <div className="value">{data.length}</div>
          <div className="subvalue">campaigns</div>
        </div>
        <div className="stat-card">
          <h3>Total Calls</h3>
          <div className="value">{totalCalls.toLocaleString()}</div>
          <div className="subvalue">across all batches</div>
        </div>
        <div className="stat-card">
          <h3>Total Credits</h3>
          <div className="value">{totalCredits.toLocaleString()}</div>
          <div className="subvalue">ElevenLabs credits</div>
        </div>
        <div className="stat-card">
          <h3>Total Cost</h3>
          <div className="value cost">${(totalCost / 100).toFixed(2)}</div>
          <div className="subvalue">Your cost</div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-container">
          <h2>Calls by Batch</h2>
          <Bar data={callsChartData} options={barOptions} />
        </div>
        <div className="chart-container">
          <h2>Cost by Batch</h2>
          <Bar data={costChartData} options={costOptions} />
        </div>
        <div className="chart-container">
          <h2>Success Rate by Batch</h2>
          <Bar data={successChartData} options={successOptions} />
        </div>
      </div>

      <div className="table-container">
        <h2>Batch Performance Details</h2>
        <table>
          <thead>
            <tr>
              <th>Batch Name</th>
              <th>Calls</th>
              <th>Credits</th>
              <th>Cost</th>
              <th>Avg/Call</th>
              <th>Success Rate</th>
              <th>Response Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, index) => (
              <tr key={index}>
                <td><strong>{d.batch_call_name || d.batch_call_id}</strong></td>
                <td>{d.total_calls}</td>
                <td>{d.total_credits_used.toLocaleString()}</td>
                <td className="cost">${(d.total_cost / 100).toFixed(2)}</td>
                <td>${d.average_cost_per_call.toFixed(2)}</td>
                <td>{d.success_rate.toFixed(1)}%</td>
                <td>{d.response_rate.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default BatchView;

